import React, { useState, useMemo } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Search,
  Filter,
  Download,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Lock,
  Eye,
  FileText,
  Sliders,
  Users,
  Layers,
  TrendingUp,
  DollarSign,
  Database,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  Plus,
  RefreshCw,
  ExternalLink,
  Hash,
  Bell,
  Play,
  Share2,
  FileSpreadsheet,
  FileCode,
  Archive,
  UserCheck,
  Info
} from 'lucide-react';
import { AppView } from '../../types';

interface AuditReportsManagementProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type ReportType = 'Audit' | 'Operations' | 'Security' | 'Compliance';
export type ReportStatus = 'Ready' | 'Processing' | 'Failed';
export type ScheduleStatus = 'Active' | 'Paused' | 'Failed';

export interface ReportItem {
  id: string;
  name: string;
  type: ReportType;
  generatedDate: string;
  owner: string;
  status: ReportStatus;
  sizeBytes?: string;
  sha256Hash: string;
  description: string;
  executiveSummary: string;
  dataPointsCount: number;
  format: 'PDF' | 'CSV' | 'JSON' | 'ZIP';
}

export interface ScheduledExportItem {
  id: string;
  report: string;
  destination: string;
  schedule: string;
  owner: string;
  lastRun: string;
  nextRun: string;
  status: ScheduleStatus;
  destinationDetails?: string;
  format: 'CSV' | 'JSON' | 'PDF';
}

export const AuditReportsManagement: React.FC<AuditReportsManagementProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  // Navigation active tab
  const [selectedDomain, setSelectedDomain] = useState<string>('operations');

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL');
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  // Modals & Drawers
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false);
  const [showNewScheduleModal, setShowNewScheduleModal] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduledExportItem | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Form State for Report Generation
  const [newReportName, setNewReportName] = useState<string>('Financial Data Quality Summary');
  const [newReportType, setNewReportType] = useState<ReportType>('Audit');
  const [newReportFormat, setNewReportFormat] = useState<'PDF' | 'CSV' | 'JSON' | 'ZIP'>('PDF');
  const [newReportScope, setNewReportScope] = useState<string>('Production (All Nodes)');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Form State for Scheduled Export
  const [newScheduleReport, setNewScheduleReport] = useState<string>('Daily Settlement Recon');
  const [newScheduleDestination, setNewScheduleDestination] = useState<string>('Not configured');
  const [newScheduleFreq, setNewScheduleFreq] = useState<string>('Daily at 01:00 UTC');
  const [newScheduleOwner, setNewScheduleOwner] = useState<string>('Finance Ops');
  const [newScheduleFormat, setNewScheduleFormat] = useState<'CSV' | 'JSON' | 'PDF'>('CSV');

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3800);
  };

  // =========================================================================
  // 1. CANONICAL REPORT LIBRARY DATASET (Strict YamooPay Reference)
  // =========================================================================
  const [reportLibrary, setReportLibrary] = useState<ReportItem[]>([
    {
      id: 'rep-001',
      name: 'Financial Data Quality Summary',
      type: 'Audit',
      generatedDate: '2023-10-24 14:30',
      owner: 'S. Connor',
      status: 'Ready',
      sizeBytes: '4.2 MB',
      sha256Hash: 'a71e8d99c42b109e8841a0293841029384102938410293841029384102938410',
      description: 'System-wide validation of financial ledger invariants, balance reconciliation, and floating-point precision tolerances across all currency clearing accounts.',
      executiveSummary: 'Zero balance anomalies detected across 48,219 settlement entries. All ECB & Fed target accounts balanced within 0.0001 precision threshold.',
      dataPointsCount: 48219,
      format: 'PDF'
    },
    {
      id: 'rep-002',
      name: 'Financial Ruleset Change Report',
      type: 'Operations',
      generatedDate: '2023-10-23 08:15',
      owner: 'System',
      status: 'Ready',
      sizeBytes: '1.8 MB',
      sha256Hash: 'b412c980ae182103859841804918230491823094812304981203948120394812',
      description: 'Audit report tracing all automated and manual parameter mutations to fiscal rulesets, tax brackets, and withholding schedules.',
      executiveSummary: '3 statutory ruleset updates deployed to Staging and Production. Dual-approval signatures verified for French SAS and UK VAT rules.',
      dataPointsCount: 1420,
      format: 'PDF'
    },
    {
      id: 'rep-003',
      name: 'Access Review Report',
      type: 'Security',
      generatedDate: '2023-10-20 16:45',
      owner: 'A. Smith',
      status: 'Failed',
      sizeBytes: '0 KB',
      sha256Hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      description: 'Quarterly user access certification across IAM roles, privileged break-glass tokens, and API credentials.',
      executiveSummary: 'Generation failed due to timeout communicating with legacy LDAP replica node. Re-trigger recommended.',
      dataPointsCount: 0,
      format: 'PDF'
    },
    {
      id: 'rep-004',
      name: 'Security Incident Summary',
      type: 'Security',
      generatedDate: '2023-10-18 09:00',
      owner: 'System',
      status: 'Ready',
      sizeBytes: '2.4 MB',
      sha256Hash: 'c984120938410293841209384120938401928340192834019283401928340192',
      description: 'SOC 2 Type II compliant summary of security telemetry, WAF block events, rate-limiting triggers, and zero-day patch applications.',
      executiveSummary: 'No Tier 1 breaches. 14 brute-force login attempts mitigated at Edge WAF level. Zero data exfiltration events.',
      dataPointsCount: 12890,
      format: 'PDF'
    },
    {
      id: 'rep-005',
      name: 'AI Evaluation Summary',
      type: 'Compliance',
      generatedDate: '2023-10-15 11:20',
      owner: 'R. Vance',
      status: 'Processing',
      sizeBytes: 'Pending...',
      sha256Hash: 'calculating...',
      description: 'Evaluation benchmark for sovereign AI risk models, hallucination benchmarks, latency distributions, and fair-lending algorithmic compliance.',
      executiveSummary: 'Batch evaluation currently in progress (84% complete) on Sovereign Inference Node eu-west-1.',
      dataPointsCount: 8500,
      format: 'JSON'
    },
    {
      id: 'rep-006',
      name: 'Backup & Restore Verification',
      type: 'Operations',
      generatedDate: '2023-10-12 10:00',
      owner: 'System',
      status: 'Ready',
      sizeBytes: '5.1 MB',
      sha256Hash: 'd129038401928340192834019283401928340192834019283401928340192834',
      description: 'Automated disaster recovery drill report verifying point-in-time recovery of transactional ledgers into sandboxed replica clusters.',
      executiveSummary: 'Recovery completed in 4 minutes 12 seconds. RTO achieved < 15 mins, RPO achieved < 1 sec. Hash validation 100% matched.',
      dataPointsCount: 65400,
      format: 'PDF'
    },
    {
      id: 'rep-007',
      name: 'Provider Availability Report',
      type: 'Operations',
      generatedDate: '2023-10-10 13:15',
      owner: 'System',
      status: 'Ready',
      sizeBytes: '3.6 MB',
      sha256Hash: 'f412c980ae182103859841804918230491823094812304981203948120394812',
      description: 'Monthly uptime, latency percentiles (p50, p95, p99), and round-trip degradation matrix across downstream payment clearing rails.',
      executiveSummary: 'Core rails maintained 99.994% uptime. Average settlement acknowledgement latency: 142ms.',
      dataPointsCount: 192000,
      format: 'CSV'
    },
    {
      id: 'rep-008',
      name: 'Compliance Evidence Review',
      type: 'Compliance',
      generatedDate: '2023-10-05 09:30',
      owner: 'L. Croft',
      status: 'Ready',
      sizeBytes: '8.7 MB',
      sha256Hash: '9841209384102938412093841209384019283401928340192834019283401928',
      description: 'Consolidated evidence pack mapping technical controls against ISO/IEC 27001, GDPR Article 32, and statutory banking directives.',
      executiveSummary: 'All 114 Annex A controls verified with cryptographic timestamp proof. Audit pack signed for statutory external assessors.',
      dataPointsCount: 340,
      format: 'ZIP'
    },
    {
      id: 'rep-009',
      name: 'Release Certification Report',
      type: 'Audit',
      generatedDate: '2023-10-01 16:00',
      owner: 'S. Connor',
      status: 'Ready',
      sizeBytes: '2.1 MB',
      sha256Hash: 'e4b109d948120394812039481203948120394812039481203948120394812039',
      description: 'Formal production release certification containing commit hashes, container SBOM scans, regression test results, and rollback contingency sign-offs.',
      executiveSummary: 'Release v2.4.1 certified without exceptions. 0 CVEs detected in production images. 100% test pass rate.',
      dataPointsCount: 812,
      format: 'PDF'
    }
  ]);

  // =========================================================================
  // 2. SCHEDULED EXPORTS DATASET (Strict YamooPay Reference)
  // =========================================================================
  const [scheduledExports, setScheduledExports] = useState<ScheduledExportItem[]>([
    {
      id: 'sch-001',
      report: 'Daily Settlement Recon',
      destination: 'Not configured',
      schedule: 'Daily at 01:00 UTC',
      owner: 'Finance Ops',
      lastRun: '2023-10-24 01:00',
      nextRun: '2023-10-25 01:00',
      status: 'Active',
      destinationDetails: 'No external endpoint or webhook attached. Stored in internal local buffer until destination is assigned.',
      format: 'CSV'
    },
    {
      id: 'sch-002',
      report: 'Weekly Risk Rollup',
      destination: 'Demo destination',
      schedule: 'Weekly, Monday 08:00 UTC',
      owner: 'System',
      lastRun: '2023-10-23 08:00',
      nextRun: '2023-10-30 08:00',
      status: 'Active',
      destinationDetails: 'Internal WORM compliance store (SHA-256 sealed immutable archive).',
      format: 'PDF'
    }
  ]);

  // Filtered Reports
  const filteredReports = useMemo(() => {
    return reportLibrary.filter((item) => {
      // 1. Search Query
      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const matches =
          item.name.toLowerCase().includes(q) ||
          item.type.toLowerCase().includes(q) ||
          item.owner.toLowerCase().includes(q) ||
          item.status.toLowerCase().includes(q);
        if (!matches) return false;
      }

      // 2. Type filter
      if (selectedTypeFilter !== 'ALL' && item.type !== selectedTypeFilter) {
        return false;
      }

      // 3. Status filter
      if (selectedStatusFilter !== 'ALL' && item.status !== selectedStatusFilter) {
        return false;
      }

      return true;
    });
  }, [reportLibrary, searchQuery, selectedTypeFilter, selectedStatusFilter]);

  // Handle Generating a New Report
  const handleGenerateReportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);

    setTimeout(() => {
      const newId = `rep-0${reportLibrary.length + 1}`;
      const now = new Date();
      const timestamp = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')} ${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`;

      const createdReport: ReportItem = {
        id: newId,
        name: newReportName,
        type: newReportType,
        generatedDate: timestamp,
        owner: 'Security Auditor (You)',
        status: 'Ready',
        sizeBytes: '3.4 MB',
        sha256Hash: Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join(''),
        description: `Ad-hoc report generation requested for ${newReportScope}. Verified with RBAC authorization token.`,
        executiveSummary: `Generated successfully under WORM storage policy. All compliance constraints and audit checkpoints verified.`,
        dataPointsCount: Math.floor(Math.random() * 25000) + 1200,
        format: newReportFormat
      };

      setReportLibrary([createdReport, ...reportLibrary]);
      setIsGenerating(false);
      setShowGenerateModal(false);
      triggerToast(`Report "${newReportName}" generated successfully and logged to Audit Trail.`);
    }, 1200);
  };

  // Handle Adding a New Schedule
  const handleAddScheduleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newId = `sch-00${scheduledExports.length + 1}`;
    const now = new Date();
    const lastRunTime = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')} 00:00`;
    const nextRunTime = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate() + 1).padStart(2, '0')} 00:00`;

    const newSchedule: ScheduledExportItem = {
      id: newId,
      report: newScheduleReport,
      destination: newScheduleDestination,
      schedule: newScheduleFreq,
      owner: newScheduleOwner,
      lastRun: lastRunTime,
      nextRun: nextRunTime,
      status: 'Active',
      destinationDetails:
        newScheduleDestination === 'Not configured'
          ? 'Destination not configured yet. Output cached in internal pipeline buffer.'
          : 'Dedicated compliance export destination.',
      format: newScheduleFormat
    };

    setScheduledExports([...scheduledExports, newSchedule]);
    setShowNewScheduleModal(false);
    triggerToast(`Scheduled export "${newScheduleReport}" registered successfully.`);
  };

  // Trigger export download with audit log registration
  const handleDownloadReport = (report: ReportItem) => {
    triggerToast(`RBAC verified: Downloaded ${report.name} (${report.format}). Event logged to Audit Trail.`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains with Operations & Risk)   */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F4F4F] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Administrator
            </span>
          </div>
        </div>

        {/* Navigation items */}
        <div className="flex-1 py-4 px-3 space-y-1">
          <button
            onClick={() => {
              setSelectedDomain('overview');
              onNavigateToView('admin-super-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Layers className="w-4 h-4" />
            <span>Overview</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('customers');
              onNavigateToView('admin-organizations');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Customers & Support</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('product');
              onNavigateToView('cpo-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Product & Growth</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('revenue');
              onNavigateToView('admin-monetization');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <DollarSign className="w-4 h-4" />
            <span>Revenue & Finance</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('data');
              onNavigateToView('admin-ai-platform');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Database className="w-4 h-4" />
            <span>Data & AI</span>
          </button>

          {/* ACTIVE: Operations & Risk (Mint green background matching screenshot) */}
          <div>
            <button
              onClick={() => setSelectedDomain('operations')}
              className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold bg-[#E6F7F0] text-[#0E7044] transition-all cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
              <span>Operations & Risk</span>
            </button>

            {/* Sub-menu */}
            <div className="ml-4 pl-3 border-l-2 border-[#0E7044] my-1 space-y-1">
              <button
                onClick={() => onNavigateToView('admin-compliance')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                Compliance Monitor
              </button>
              <button
                onClick={() => onNavigateToView('admin-audit')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                Audit Logs
              </button>
              <button
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#0E7044] bg-[#E6F7F0]/60 transition-colors cursor-pointer"
              >
                Audit & Reports
              </button>
              <button
                onClick={() => onNavigateToView('admin-system-health')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                System Health
              </button>
              <button
                onClick={() => onNavigateToView('ciso-security-command-center')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                CISO Security
              </button>
            </div>
          </div>

          <button
            onClick={() => {
              setSelectedDomain('configuration');
              onNavigateToView('admin-configuration');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Sliders className="w-4 h-4" />
            <span>Configuration</span>
          </button>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F4F4F] text-white font-bold text-xs flex items-center justify-center">
              AD
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Security Auditor</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">RBAC & WORM Active</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar with Breadcrumb and Operations Search (Screenshot match) */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20 gap-6">
          <div className="flex items-center gap-6 flex-1 max-w-2xl">
            <span className="text-xs font-bold text-[#0F172A] whitespace-nowrap">Control Center</span>
            <div className="relative w-full max-w-md">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search operations..."
                className="w-full pl-9 pr-4 py-1.5 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] rounded-xl text-xs text-[#0F172A] placeholder-[#94A3B8] outline-none focus:border-[#0F4F4F] focus:ring-1 focus:ring-[#0F4F4F] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <button
              onClick={() => triggerToast('WORM Compliance: All reports protected by cryptographic integrity seals.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Integrity Sealed"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              onClick={() => triggerToast('Active timezone: UTC. All scheduled exports synchronize to midnight UTC.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Timezone Sync"
            >
              <Calendar className="w-4 h-4" />
            </button>
            <button
              onClick={() => triggerToast('Report pipeline: 0 failed batch exports in last 24h.')}
              className="relative text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Alerts"
            >
              <Bell className="w-4 h-4" />
              <span className="w-2 h-2 bg-red-500 rounded-full absolute top-1.5 right-1.5 ring-2 ring-white"></span>
            </button>
            <div className="w-8 h-8 rounded-full bg-[#E2E8F0] flex items-center justify-center text-[#475569] text-xs font-bold">
              AD
            </div>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-8 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* =============================================================== */}
          {/* TITLE & DESCRIPTION (Exact match to screenshot)                 */}
          {/* =============================================================== */}
          <div>
            <h2 className="text-2xl font-black text-[#0F172A] tracking-tight">
              Audit & Reports
            </h2>
            <p className="text-xs text-[#64748B] mt-1">
              Manage report library and scheduled data exports for risk and operations.
            </p>
          </div>

          {/* =============================================================== */}
          {/* CARD 1: REPORT LIBRARY (Exact reproduction of screenshot)       */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            {/* Card Header with Title, Filter, and Generate New Button */}
            <div className="p-5 border-b border-[#F1F5F9] flex items-center justify-between flex-wrap gap-4">
              <h3 className="text-base font-bold text-[#0F172A]">
                Report Library
              </h3>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilterModal(true)}
                  className="px-4 py-2 bg-white hover:bg-[#F8FAFC] text-[#0F172A] border border-[#CBD5E1] rounded-xl text-xs font-semibold shadow-2xs transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <Filter className="w-3.5 h-3.5 text-[#64748B]" />
                  <span>Filter</span>
                  {(selectedTypeFilter !== 'ALL' || selectedStatusFilter !== 'ALL') && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0E7044]"></span>
                  )}
                </button>

                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Generate New</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B] font-medium text-[11px]">
                    <th className="py-3.5 px-6 font-medium">Name</th>
                    <th className="py-3.5 px-6 font-medium">Type</th>
                    <th className="py-3.5 px-6 font-medium">Generated Date</th>
                    <th className="py-3.5 px-6 font-medium">Owner</th>
                    <th className="py-3.5 px-6 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {filteredReports.map((report) => (
                    <tr
                      key={report.id}
                      onClick={() => setSelectedReport(report)}
                      className="hover:bg-[#F8FAFC] transition-colors cursor-pointer group"
                    >
                      {/* Name */}
                      <td className="py-4 px-6 font-bold text-[#0F172A] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#64748B] group-hover:text-[#0F4F4F] transition-colors shrink-0" />
                          <span className="group-hover:text-[#0F4F4F] transition-colors">
                            {report.name}
                          </span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="py-4 px-6 text-[#475569] whitespace-nowrap">
                        {report.type}
                      </td>

                      {/* Generated Date */}
                      <td className="py-4 px-6 font-mono text-[11px] text-[#475569] whitespace-nowrap">
                        {report.generatedDate}
                      </td>

                      {/* Owner */}
                      <td className="py-4 px-6 text-[#475569] whitespace-nowrap">
                        {report.owner}
                      </td>

                      {/* Status (Pills matching screenshot) */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        {report.status === 'Ready' && (
                          <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-[#DCFCE7] text-[#15803D]">
                            Ready
                          </span>
                        )}
                        {report.status === 'Processing' && (
                          <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-[#F1F5F9] text-[#475569]">
                            Processing
                          </span>
                        )}
                        {report.status === 'Failed' && (
                          <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-[#FEE2E2] text-[#B91C1C]">
                            Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {filteredReports.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-[#64748B]">
                        <Search className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
                        <span className="font-bold text-sm text-[#0F172A] block">No reports found</span>
                        <span className="text-xs text-[#94A3B8] block mt-0.5">
                          Try adjusting your search terms or filter selection.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* =============================================================== */}
          {/* CARD 2: SCHEDULED EXPORTS (Exact reproduction of screenshot)    */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            {/* Card Header with Title and + New Schedule Button */}
            <div className="p-5 border-b border-[#F1F5F9] flex items-center justify-between flex-wrap gap-4">
              <h3 className="text-base font-bold text-[#0F172A]">
                Scheduled Exports
              </h3>

              <button
                onClick={() => setShowNewScheduleModal(true)}
                className="px-4 py-2 bg-white hover:bg-[#F8FAFC] text-[#0F172A] border border-[#CBD5E1] rounded-xl text-xs font-semibold shadow-2xs transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5 text-[#0F172A]" />
                <span>New Schedule</span>
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B] font-medium text-[11px]">
                    <th className="py-3.5 px-6 font-medium">Report</th>
                    <th className="py-3.5 px-6 font-medium">Destination</th>
                    <th className="py-3.5 px-6 font-medium">Schedule</th>
                    <th className="py-3.5 px-6 font-medium">Owner</th>
                    <th className="py-3.5 px-6 font-medium">Last Run</th>
                    <th className="py-3.5 px-6 font-medium">Next Run</th>
                    <th className="py-3.5 px-6 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {scheduledExports.map((sch) => (
                    <tr
                      key={sch.id}
                      onClick={() => setSelectedSchedule(sch)}
                      className="hover:bg-[#F8FAFC] transition-colors cursor-pointer group"
                    >
                      {/* Report */}
                      <td className="py-4 px-6 font-bold text-[#0F172A] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#64748B] group-hover:text-[#0F4F4F] transition-colors shrink-0" />
                          <span className="group-hover:text-[#0F4F4F] transition-colors">
                            {sch.report}
                          </span>
                        </div>
                      </td>

                      {/* Destination (With specific "Not configured" or "Demo destination") */}
                      <td className="py-4 px-6 text-[#475569] whitespace-nowrap">
                        {sch.destination === 'Not configured' ? (
                          <span className="text-[#64748B] italic">Not configured</span>
                        ) : (
                          <span>{sch.destination}</span>
                        )}
                      </td>

                      {/* Schedule */}
                      <td className="py-4 px-6 text-[#475569] whitespace-nowrap">
                        {sch.schedule}
                      </td>

                      {/* Owner */}
                      <td className="py-4 px-6 text-[#475569] whitespace-nowrap">
                        {sch.owner}
                      </td>

                      {/* Last Run (Two-line timestamp match from screenshot) */}
                      <td className="py-4 px-6 font-mono text-[11px] text-[#475569] whitespace-nowrap">
                        <div>{sch.lastRun.split(' ')[0]}</div>
                        <div className="text-[#94A3B8]">{sch.lastRun.split(' ')[1] || '00:00'}</div>
                      </td>

                      {/* Next Run (Two-line timestamp match from screenshot) */}
                      <td className="py-4 px-6 font-mono text-[11px] text-[#475569] whitespace-nowrap">
                        <div>{sch.nextRun.split(' ')[0]}</div>
                        <div className="text-[#94A3B8]">{sch.nextRun.split(' ')[1] || '00:00'}</div>
                      </td>

                      {/* Status */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        {sch.status === 'Active' && (
                          <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-[#DCFCE7] text-[#15803D]">
                            Active
                          </span>
                        )}
                        {sch.status === 'Paused' && (
                          <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-[#FEF3C7] text-[#B45309]">
                            Paused
                          </span>
                        )}
                        {sch.status === 'Failed' && (
                          <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-[#FEE2E2] text-[#B91C1C]">
                            Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* 3. REPORT VIEWER & INTEGRITY DRAWER                            */}
      {/* =============================================================== */}
      {selectedReport && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-end">
          <div className="bg-white w-full max-w-xl h-full shadow-2xl border-l border-[#E2E8F0] p-6 space-y-6 overflow-y-auto flex flex-col justify-between animate-in slide-in-from-right duration-300">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between pb-4 border-b border-[#F1F5F9]">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-gray-100 text-[#0F172A] rounded text-[11px] font-mono font-bold">
                      {selectedReport.id}
                    </span>
                    <span className="px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB] rounded text-[11px] font-semibold">
                      {selectedReport.type}
                    </span>
                    {selectedReport.status === 'Ready' && (
                      <span className="px-2 py-0.5 bg-[#DCFCE7] text-[#15803D] rounded text-[11px] font-semibold">
                        Ready
                      </span>
                    )}
                    {selectedReport.status === 'Failed' && (
                      <span className="px-2 py-0.5 bg-[#FEE2E2] text-[#B91C1C] rounded text-[11px] font-semibold">
                        Failed
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mt-2">
                    {selectedReport.name}
                  </h3>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    Generated: <span className="font-mono text-[#0F172A]">{selectedReport.generatedDate} UTC</span> by <span className="font-semibold text-[#0F172A]">{selectedReport.owner}</span>
                  </p>
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Integrity Seal */}
              <div className="p-3.5 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl flex items-start gap-2.5 text-xs text-[#166534]">
                <ShieldCheck className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold block">Cryptographic WORM Integrity Verified</span>
                  <span className="text-[11px] text-[#15803D] mt-0.5 block">
                    This report payload is tamper-proof and sealed in accordance with statutory audit policies.
                  </span>
                </div>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Format</span>
                  <span className="font-bold text-[#0F172A] text-xs mt-1 block">{selectedReport.format}</span>
                </div>
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">File Size</span>
                  <span className="font-mono font-bold text-[#0F172A] text-xs mt-1 block">{selectedReport.sizeBytes}</span>
                </div>
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Records Checked</span>
                  <span className="font-mono font-bold text-[#0F172A] text-xs mt-1 block">{selectedReport.dataPointsCount.toLocaleString()}</span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1.5 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                  Report Scope & Parameters
                </label>
                <p className="text-[#334155] leading-relaxed bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  {selectedReport.description}
                </p>
              </div>

              {/* Executive Summary */}
              <div className="space-y-1.5 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                  Executive Summary
                </label>
                <p className="text-[#0F172A] font-medium leading-relaxed bg-[#EFF6FF]/60 border border-[#BFDBFE] p-3 rounded-xl">
                  {selectedReport.executiveSummary}
                </p>
              </div>

              {/* SHA-256 Hash Proof */}
              <div className="space-y-1.5 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5 text-[#64748B]" />
                  <span>SHA-256 Checksum</span>
                </label>
                <div className="font-mono text-[10px] text-[#475569] bg-[#F1F5F9] p-2.5 rounded-xl border border-[#E2E8F0] break-all select-all">
                  {selectedReport.sha256Hash}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-[#F1F5F9] text-xs">
              <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
                <Lock className="w-3 h-3" />
                <span>RBAC Signed</span>
              </span>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(JSON.stringify(selectedReport, null, 2));
                    triggerToast(`Copied report metadata for ${selectedReport.id}`);
                  }}
                  className="px-3 py-1.5 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl"
                >
                  Copy JSON
                </button>
                {selectedReport.status === 'Ready' && (
                  <button
                    onClick={() => handleDownloadReport(selectedReport)}
                    className="px-4 py-1.5 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl flex items-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download {selectedReport.format}</span>
                  </button>
                )}
                {selectedReport.status === 'Failed' && (
                  <button
                    onClick={() => {
                      triggerToast(`Re-triggering report generation for ${selectedReport.name}...`);
                      setSelectedReport({ ...selectedReport, status: 'Processing' });
                    }}
                    className="px-4 py-1.5 bg-[#B91C1C] hover:bg-[#991B1B] text-white font-bold rounded-xl flex items-center gap-1.5"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Retry Generation</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 4. SCHEDULED EXPORT DETAILS DRAWER                              */}
      {/* =============================================================== */}
      {selectedSchedule && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-end">
          <div className="bg-white w-full max-w-lg h-full shadow-2xl border-l border-[#E2E8F0] p-6 space-y-6 overflow-y-auto flex flex-col justify-between animate-in slide-in-from-right duration-300">
            <div className="space-y-6">
              <div className="flex items-start justify-between pb-4 border-b border-[#F1F5F9]">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-gray-100 text-[#0F172A] rounded text-[11px] font-mono font-bold">
                      {selectedSchedule.id}
                    </span>
                    <span className="px-2 py-0.5 bg-[#DCFCE7] text-[#15803D] rounded text-[11px] font-semibold">
                      {selectedSchedule.status}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mt-2">
                    {selectedSchedule.report}
                  </h3>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    Schedule: <span className="font-semibold text-[#0F172A]">{selectedSchedule.schedule}</span>
                  </p>
                </div>
                <button
                  onClick={() => setSelectedSchedule(null)}
                  className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Schedule Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Owner</span>
                  <span className="font-bold text-[#0F172A] text-xs mt-1 block">{selectedSchedule.owner}</span>
                </div>
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Format</span>
                  <span className="font-bold text-[#0F172A] text-xs mt-1 block">{selectedSchedule.format}</span>
                </div>
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Last Run</span>
                  <span className="font-mono text-[#0F172A] text-xs mt-1 block">{selectedSchedule.lastRun} UTC</span>
                </div>
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Next Run</span>
                  <span className="font-mono text-[#0F172A] text-xs mt-1 block">{selectedSchedule.nextRun} UTC</span>
                </div>
              </div>

              {/* Destination status */}
              <div className="space-y-1.5 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                  Export Destination Endpoint
                </label>
                <div className="bg-[#F8FAFC] p-3.5 rounded-xl border border-[#E2E8F0] space-y-1">
                  <div className="font-bold text-[#0F172A]">
                    {selectedSchedule.destination}
                  </div>
                  <div className="text-[11px] text-[#64748B]">
                    {selectedSchedule.destinationDetails}
                  </div>
                </div>
              </div>

              {selectedSchedule.destination === 'Not configured' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 text-xs text-amber-800">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold block">No Destination Configured</span>
                    <span className="text-[11px] text-amber-700 block mt-0.5">
                      Exports will generate and sit in the local buffer until an approved compliance endpoint or email target is configured.
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-[#F1F5F9] text-xs">
              <button
                onClick={() => {
                  const updatedStatus = selectedSchedule.status === 'Active' ? 'Paused' : 'Active';
                  setSelectedSchedule({ ...selectedSchedule, status: updatedStatus });
                  setScheduledExports(
                    scheduledExports.map((s) =>
                      s.id === selectedSchedule.id ? { ...s, status: updatedStatus } : s
                    )
                  );
                  triggerToast(`Schedule status updated to ${updatedStatus}.`);
                }}
                className="px-3 py-1.5 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl"
              >
                {selectedSchedule.status === 'Active' ? 'Pause Schedule' : 'Resume Schedule'}
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    triggerToast(`Triggered manual execution for "${selectedSchedule.report}". Exporting now...`);
                    setSelectedSchedule(null);
                  }}
                  className="px-4 py-1.5 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl flex items-center gap-1.5"
                >
                  <Play className="w-3 h-3" />
                  <span>Run Now</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 5. GENERATE NEW REPORT MODAL (Strictly allowed report types)    */}
      {/* =============================================================== */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">Generate New Report</h3>
                <p className="text-[11px] text-[#64748B]">Select an official YamooPay risk or compliance report</p>
              </div>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleGenerateReportSubmit} className="space-y-4 text-xs">
              {/* Report Type Selector (Strict YamooPay List) */}
              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Report Name & Type</label>
                <select
                  value={newReportName}
                  onChange={(e) => {
                    setNewReportName(e.target.value);
                    // Automatically assign corresponding domain
                    if (e.target.value.includes('Financial Data') || e.target.value.includes('Release Certification')) {
                      setNewReportType('Audit');
                    } else if (e.target.value.includes('Ruleset') || e.target.value.includes('Backup') || e.target.value.includes('Provider')) {
                      setNewReportType('Operations');
                    } else if (e.target.value.includes('Access') || e.target.value.includes('Incident')) {
                      setNewReportType('Security');
                    } else {
                      setNewReportType('Compliance');
                    }
                  }}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                >
                  <option value="Financial Data Quality Summary">Financial Data Quality Summary (Audit)</option>
                  <option value="Financial Ruleset Change Report">Financial Ruleset Change Report (Operations)</option>
                  <option value="Access Review Report">Access Review Report (Security)</option>
                  <option value="Security Incident Summary">Security Incident Summary (Security)</option>
                  <option value="AI Evaluation Summary">AI Evaluation Summary (Compliance)</option>
                  <option value="Backup & Restore Verification">Backup & Restore Verification (Operations)</option>
                  <option value="Provider Availability Report">Provider Availability Report (Operations)</option>
                  <option value="Compliance Evidence Review">Compliance Evidence Review (Compliance)</option>
                  <option value="Release Certification Report">Release Certification Report (Audit)</option>
                </select>
              </div>

              {/* Scope Selector */}
              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Environment & Scope</label>
                <select
                  value={newReportScope}
                  onChange={(e) => setNewReportScope(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                >
                  <option value="Production (All Nodes)">Production (All Nodes)</option>
                  <option value="Production (EU-Central-1 Frankfurt)">Production (EU-Central-1 Frankfurt)</option>
                  <option value="Production (US-East-1 N. Virginia)">Production (US-East-1 N. Virginia)</option>
                  <option value="Staging Environment">Staging Environment</option>
                  <option value="Full System-Wide Multi-Region">Full System-Wide Multi-Region</option>
                </select>
              </div>

              {/* Output Format */}
              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Export Format</label>
                <div className="grid grid-cols-4 gap-2">
                  {(['PDF', 'CSV', 'JSON', 'ZIP'] as const).map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => setNewReportFormat(fmt)}
                      className={`py-2 rounded-xl border text-xs font-bold transition-colors cursor-pointer ${
                        newReportFormat === fmt
                          ? 'bg-[#0F4F4F] border-[#0F4F4F] text-white'
                          : 'bg-[#F8FAFC] border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'
                      }`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>

              {/* RBAC Notice */}
              <div className="p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl flex items-center gap-2 text-xs text-[#1E40AF]">
                <UserCheck className="w-4 h-4 text-[#2563EB] shrink-0" />
                <span className="text-[11px]">
                  RBAC Role Verified: <strong>Administrator / Security Auditor</strong>. Action will be sealed in Audit Logs.
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-2 border border-[#CBD5E1] rounded-xl text-xs font-bold text-[#475569] hover:bg-[#F8FAFC]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isGenerating}
                  className="px-5 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Generate Report</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 6. NEW SCHEDULED EXPORT MODAL                                   */}
      {/* =============================================================== */}
      {showNewScheduleModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">Schedule Recurring Export</h3>
                <p className="text-[11px] text-[#64748B]">Automate report compilation and dispatch</p>
              </div>
              <button
                onClick={() => setShowNewScheduleModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddScheduleSubmit} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Target Report</label>
                <select
                  value={newScheduleReport}
                  onChange={(e) => setNewScheduleReport(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                >
                  <option value="Daily Settlement Recon">Daily Settlement Recon</option>
                  <option value="Weekly Risk Rollup">Weekly Risk Rollup</option>
                  <option value="Financial Data Quality Summary">Financial Data Quality Summary</option>
                  <option value="Financial Ruleset Change Report">Financial Ruleset Change Report</option>
                  <option value="Security Incident Summary">Security Incident Summary</option>
                  <option value="Compliance Evidence Review">Compliance Evidence Review</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Destination</label>
                <select
                  value={newScheduleDestination}
                  onChange={(e) => setNewScheduleDestination(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                >
                  <option value="Not configured">Not configured</option>
                  <option value="Demo destination">Demo destination (Internal WORM Store)</option>
                  <option value="Local Compliance Buffer">Local Compliance Buffer</option>
                </select>
                <p className="text-[10px] text-[#64748B] italic">
                  Note: If external storage is unconfigured, destination remains "Not configured".
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="font-bold text-[#334155] block">Frequency</label>
                  <select
                    value={newScheduleFreq}
                    onChange={(e) => setNewScheduleFreq(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                  >
                    <option value="Daily at 01:00 UTC">Daily at 01:00 UTC</option>
                    <option value="Weekly, Monday 08:00 UTC">Weekly, Monday 08:00 UTC</option>
                    <option value="Monthly, 1st at 00:00 UTC">Monthly, 1st at 00:00 UTC</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="font-bold text-[#334155] block">Owner / Team</label>
                  <input
                    type="text"
                    value={newScheduleOwner}
                    onChange={(e) => setNewScheduleOwner(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Format</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['CSV', 'JSON', 'PDF'] as const).map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => setNewScheduleFormat(fmt)}
                      className={`py-2 rounded-xl border text-xs font-bold transition-colors cursor-pointer ${
                        newScheduleFormat === fmt
                          ? 'bg-[#0F4F4F] border-[#0F4F4F] text-white'
                          : 'bg-[#F8FAFC] border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'
                      }`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => setShowNewScheduleModal(false)}
                  className="px-4 py-2 border border-[#CBD5E1] rounded-xl text-xs font-bold text-[#475569] hover:bg-[#F8FAFC]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-bold flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Save Schedule</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 7. FILTER LIBRARY MODAL                                         */}
      {/* =============================================================== */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <h3 className="text-sm font-bold text-[#0F172A]">Filter Report Library</h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Report Type</label>
                <select
                  value={selectedTypeFilter}
                  onChange={(e) => setSelectedTypeFilter(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                >
                  <option value="ALL">All Types</option>
                  <option value="Audit">Audit</option>
                  <option value="Operations">Operations</option>
                  <option value="Security">Security</option>
                  <option value="Compliance">Compliance</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-[#334155] block">Generation Status</label>
                <select
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-3 py-2 text-xs font-semibold text-[#0F172A] outline-none"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="Ready">Ready</option>
                  <option value="Processing">Processing</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTypeFilter('ALL');
                    setSelectedStatusFilter('ALL');
                  }}
                  className="text-xs font-semibold text-[#64748B] hover:text-[#0F172A]"
                >
                  Reset Filters
                </button>
                <button
                  type="button"
                  onClick={() => setShowFilterModal(false)}
                  className="px-4 py-2 bg-[#0F4F4F] text-white rounded-xl text-xs font-bold"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
